import { useEffect, useMemo, useState } from 'react';
import { Search, RefreshCw, FileText } from 'lucide-react';
import adminDashboardService, { type AcessoRecente } from '@/services/adminDashboard';

const TIPOS_LOG = ['LOGIN', 'LOGOUT', 'CREATE', 'UPDATE', 'DELETE', 'VIEW', 'EXPORT', 'PRINT'];

const CORES_TIPO: Record<string, string> = {
  LOGIN: 'bg-emerald-100 text-emerald-700',
  LOGOUT: 'bg-sky-100 text-sky-700',
  CREATE: 'bg-blue-100 text-blue-700',
  UPDATE: 'bg-amber-100 text-amber-700',
  DELETE: 'bg-red-100 text-red-700',
};

/**
 * Nota: a tela original em Vue (Logs.vue) usava dados 100% ficticios
 * (mock hardcoded no componente) e nunca chamou nenhum endpoint real.
 * O backend Laravel possui um endpoint real de acessos recentes
 * (AdminDashboardController::acessosRecentes, baseado no model AccessLog),
 * entao esta pagina foi implementada usando os dados reais em vez de
 * replicar os dados falsos do Vue.
 */
export default function AdminLogs() {
  const [logs, setLogs] = useState<AcessoRecente[]>([]);
  const [loading, setLoading] = useState(false);
  const [busca, setBusca] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');

  const carregar = async () => {
    setLoading(true);
    try {
      const data = await adminDashboardService.acessosRecentes();
      setLogs(data);
    } catch (error) {
      console.error('Erro ao carregar logs', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const filtrados = useMemo(() => {
    let resultado = logs;
    if (filtroTipo) resultado = resultado.filter((l) => l.action === filtroTipo);
    if (busca) {
      const termo = busca.toLowerCase();
      resultado = resultado.filter(
        (l) => l.user?.toLowerCase().includes(termo) || l.module?.toLowerCase().includes(termo) || l.ip?.toLowerCase().includes(termo)
      );
    }
    return resultado;
  }, [logs, busca, filtroTipo]);

  return (
    <div className="rounded-lg bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <FileText className="h-6 w-6 text-sky-600" />
        <h2 className="flex-1 text-xl font-bold text-slate-800">Logs de Acesso</h2>
        <button
          type="button"
          onClick={carregar}
          disabled={loading}
          className="flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Atualizar
        </button>
      </div>

      <div className="mb-4 flex flex-wrap gap-3">
        <div className="relative w-full max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            placeholder="Buscar logs..."
            className="w-full rounded-md border border-slate-300 py-2 pl-9 pr-3 text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
          />
        </div>
        <select
          value={filtroTipo}
          onChange={(e) => setFiltroTipo(e.target.value)}
          className="rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
        >
          <option value="">Todos os tipos</option>
          {TIPOS_LOG.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-slate-500">
            <th className="py-2">Data/Hora</th>
            <th className="py-2">Usuário</th>
            <th className="py-2">Tipo</th>
            <th className="py-2">Módulo</th>
            <th className="py-2">IP</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={5} className="py-8 text-center text-slate-400">Carregando...</td></tr>
          ) : filtrados.length === 0 ? (
            <tr><td colSpan={5} className="py-8 text-center text-slate-400">Nenhum log encontrado</td></tr>
          ) : (
            filtrados.map((log, idx) => (
              <tr key={idx} className="border-b border-slate-100">
                <td className="py-2">{log.data ? new Date(log.data).toLocaleString('pt-BR') : '-'}</td>
                <td className="py-2">{log.user}</td>
                <td className="py-2">
                  <span className={`rounded-full px-2 py-1 text-xs font-medium ${CORES_TIPO[log.action] || 'bg-slate-100 text-slate-700'}`}>
                    {log.action}
                  </span>
                </td>
                <td className="py-2 text-slate-500">{log.module || '-'}</td>
                <td className="py-2 text-slate-500">{log.ip || '-'}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}
